import React, { useState } from 'react';
import PdfModalLayout from './PdfModalLayout';
import PdfLeftSection from './PdfLeftSection';
import PdfRightSection from './PdfRightSection';
import PdfFooterSection from './PdfFooterSection';
import { ActionItem } from '../../../types/button/ActionButton';
import { usePdfPrinter } from '../../../hooks/usePdfPrinter';
import { usePdfDownloader } from '../../../hooks/usePdfDownloader';
import { DropdownWithCodeDTO } from '../../../types/common/DropdownWithCodeDTO';

interface PdfModalProps {
  isOpen: boolean;
  onClose: () => void;
  documentOptions: DropdownWithCodeDTO[];
  actions?: ActionItem[];
  onViewDocument?: (option: { id: number; code: string }) => void;
  onReset?: () => void;
  base64Pdf?: string;
  fileName?: string; // ✅ nombre real del PDF (ej: "DTE_6EFA3A7A.pdf")
}

const PdfModal: React.FC<PdfModalProps> = ({
  isOpen,
  onClose,
  documentOptions,
  actions: externalActions = [],
  onViewDocument,
  onReset,
  base64Pdf,
  fileName = 'documento.pdf', // fallback
}) => {
  const [selectedType, setSelectedType] = useState<{
    id: number;
    code: string;
  } | null>(null);

  // ♻️ Hooks reutilizables
  const { printBase64Pdf } = usePdfPrinter();
  const { downloadBase64Pdf } = usePdfDownloader();

  // 🧩 Acciones internas (descargar, imprimir, cerrar)
  const internalActions: ActionItem[] = [
    {
      key: 'pdf',
      label: 'Descargar PDF',
      onClick: () => {
        if (!base64Pdf) return;
        downloadBase64Pdf(base64Pdf, fileName);
      },
    },
    {
      key: 'print',
      label: 'Imprimir',
      onClick: () => {
        if (!base64Pdf) return;
        printBase64Pdf(base64Pdf, fileName);
      },
    },
    {
      key: 'close',
      label: 'Cerrar',
      onClick: () => {
        onClose();
        if (onReset) onReset();
      },
    },
  ];

  // 🔀 Combinar acciones externas con las internas
  const finalActions: ActionItem[] = [...externalActions, ...internalActions];

  return (
    <PdfModalLayout
      maxWidth="max-w-full"
      isOpen={isOpen}
      onClose={() => {
        onClose();
        if (onReset) onReset();
      }}
      // 🧾 Mostrar el código o nombre del archivo en el título
      title={
        fileName
          ? `Documento: ${fileName.replace('.pdf', '')}`
          : 'Documento de Venta'
      }
      leftContent={
        <PdfLeftSection
          selectedType={selectedType?.id || ''}
          onChangeType={(id) => {
            const selected = documentOptions.find(
              (opt) => opt.id === Number(id)
            );
            if (selected) {
              setSelectedType({ id: selected.id, code: selected.code });
            }
          }}
          onView={() => {
            if (selectedType && onViewDocument) {
              onViewDocument(selectedType);
            }
          }}
          options={documentOptions}
        />
      }
      rightContent={<PdfRightSection base64Pdf={base64Pdf} />}
      footerContent={<PdfFooterSection actions={finalActions} />}
    />
  );
};

export default PdfModal;
